import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProfileEditor = ({ initialContent, onSave, onCancel }) => {
  const [content, setContent] = useState(initialContent);
  const [violations, setViolations] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const blockedPatterns = [
    { pattern: /\b[A-Z][a-z]+ [A-Z][a-z]+\b/g, type: 'Personal Name', suggestion: 'Remove names and use role descriptions' },
    { pattern: /\b(Google|Microsoft|Amazon|Apple|Meta|Tesla|Netflix)\b/gi, type: 'Company Name', suggestion: 'Use "a leading tech company" or similar' },
    { pattern: /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, type: 'Email Address', suggestion: 'Remove email addresses' },
    { pattern: /\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g, type: 'Phone Number', suggestion: 'Remove phone numbers' },
    { pattern: /linkedin\.com\/in\/[\w-]+/gi, type: 'LinkedIn URL', suggestion: 'Remove social media links' },
    { pattern: /\b\d{1,5}\s+[\w\s]+(?:Street|St|Avenue|Ave|Road|Rd|Boulevard|Blvd)\b/gi, type: 'Street Address', suggestion: 'Use city or region only' }
  ];

  useEffect(() => {
    validateContent(content);
  }, [content]);

  const validateContent = (text) => {
    const foundViolations = [];
    
    blockedPatterns?.forEach(({ pattern, type, suggestion }) => {
      const matches = text?.match(pattern);
      if (matches) {
        matches?.forEach(match => {
          foundViolations?.push({
            text: match,
            type,
            suggestion
          });
        });
      }
    });

    setViolations(foundViolations);
  };

  const handleSave = async () => {
    if (violations?.length > 0) {
      return;
    }

    setIsSaving(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    onSave(content);
    setIsSaving(false);
  };

  const wordCount = content?.trim()?.split(/\s+/)?.length;
  const charCount = content?.length;

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#0025cc] to-[#0025cc]">
            <Icon name="Edit" size={20} color="#FFFFFF" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl lg:text-2xl font-semibold text-[#111827]">Edit Profile</h2>
            <p className="text-sm md:text-base text-[#6B7280]">Refine your anonymous summary</p>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-2xl p-4 md:p-6 border border-[#E5E7EB]">
        <textarea
          value={content}
          onChange={(e) => setContent(e?.target?.value)}
          className="w-full min-h-[300px] md:min-h-[400px] p-4 md:p-6 text-sm md:text-base text-[#111827] border border-[#E5E7EB] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#5B2DFF] focus:border-transparent resize-none"
          placeholder="Edit your anonymous professional profile..."
        />

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#E5E7EB]">
          <div className="flex items-center gap-4 md:gap-6">
            <span className="text-xs md:text-sm text-[#6B7280]">
              <span className="font-medium text-[#111827]">{wordCount}</span> words
            </span>
            <span className="text-xs md:text-sm text-[#6B7280]">
              <span className="font-medium text-[#111827]">{charCount}</span> characters
            </span>
          </div>
          {violations?.length > 0 && (
            <div className="flex items-center gap-2">
              <Icon name="AlertTriangle" size={16} color="#EF4444" />
              <span className="text-xs md:text-sm font-medium text-[#EF4444]">
                {violations?.length} issue{violations?.length > 1 ? 's' : ''} detected
              </span>
            </div>
          )}
        </div>
      </div>
      {violations?.length > 0 && (
        <div className="bg-white rounded-2xl p-4 md:p-6 border border-[#EF4444]">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg flex items-center justify-center bg-[#EF4444]/10 flex-shrink-0">
              <Icon name="AlertTriangle" size={20} color="#EF4444" />
            </div>
            <div>
              <h3 className="text-base md:text-lg font-semibold text-[#EF4444] mb-1">Anonymity Violations</h3>
              <p className="text-sm md:text-base text-[#6B7280]">
                Please remove the following identifying information:
              </p>
            </div>
          </div>

          <div className="space-y-2 md:space-y-3">
            {violations?.map((violation, index) => (
              <div key={index} className="flex items-start gap-2 p-3 md:p-4 bg-[#FEF2F2] rounded-xl">
                <Icon name="X" size={16} color="#EF4444" className="flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm md:text-base font-medium text-[#111827]">{violation?.text}</p>
                  <p className="text-xs md:text-sm text-[#6B7280] mt-1">{violation?.suggestion}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex flex-col md:flex-row gap-3 md:gap-4">
        <Button
          variant="outline"
          size="lg"
          onClick={onCancel}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          variant="default"
          size="lg"
          loading={isSaving}
          disabled={violations?.length > 0}
          iconName="Save"
          iconPosition="left"
          onClick={handleSave}
          className="flex-1"
        >
          {isSaving ? 'Saving...' : 'Save Profile'}
        </Button>
      </div>
      <div className="flex items-start gap-2 p-3 md:p-4 bg-[#F6F7FB] rounded-xl">
        <Icon name="Info" size={16} color="#5B2DFF" className="flex-shrink-0 mt-0.5" />
        <p className="text-xs md:text-sm text-[#6B7280]">
          Your profile will be automatically validated for anonymity compliance before saving. Any identifying information will be flagged and must be removed.
        </p>
      </div>
    </div>
  );
};

export default ProfileEditor;